import React, { useEffect, useState } from "react";
import { listVersions, selectVersion, editQuestionVersion } from "../services/api";

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const time = d.toLocaleTimeString();
  return `${day}/${month}/${year}, ${time}`;
};

export default function VersionDialog({
  open,
  auditId,
  aqId,
  selectedText, // current selected text (from matrix row)
  onClose,
  onSelected,
}) {
  // UI: newest-first list
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [newText, setNewText] = useState("");

  useEffect(() => {
    if (!open || !aqId) return;
    (async () => {
      try {
        setLoading(true);
        const res = await listVersions(auditId, aqId);
        const items = Array.isArray(res?.items) ? res.items : [];
        const newestFirst = items.slice().reverse(); //reversed from backend

        setVersions(newestFirst);
        setSelectedVersionId(res?.selected_version_id ?? null);

        // Prefill new text with currently selected
        if (selectedText && String(selectedText).trim()) {
          setNewText(selectedText);
        } else {
          const sel =
            newestFirst.find((v) => v.id === res?.selected_version_id) ||
            newestFirst[0];
          setNewText(sel?.text || "");
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, auditId, aqId, selectedText]);

  const handleSelect = async (vid) => {
    try {
      await selectVersion(auditId, aqId, vid);
      onSelected?.();
    } catch (e) {
      console.error(e);
    } finally {
      onClose?.();
    }
  };

  const handleAdd = async () => {
    const text = (newText || "").trim();
    if (!text) return;
    try {
      // Appends a *new* version and selects it server-side
      await editQuestionVersion(auditId, aqId, text);
      onSelected?.();
    } catch (e) {
      console.error(e);
    } finally {
      onClose?.();
    }
  };

  if (!open) return null;

  const pressedPillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 10,
    color: "#fff",
    background: "linear-gradient(180deg, #1F3E80 0%, #162A5A 100%)",
    border: "1px solid #0f224d",
    boxShadow:
      "inset 0 3px 6px rgba(0,0,0,0.45), inset 0 -1px 0 rgba(255,255,255,0.12)",
    transform: "translateY(1px)",
    userSelect: "none",
    cursor: "default",
  };

  const itemCardStyle = (isCurrent) => ({
    border: `1px solid ${isCurrent ? "#c5d0ea" : "var(--border)"}`,
    padding: "12px 12px 10px 12px",
    borderRadius: 8,
    marginBottom: 10,
    background: isCurrent ? "rgba(31,62,128,0.10)" : "#fff",
  });

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose?.();
      }}
    >
      <div className="modal-card" style={{ width: 760, maxWidth: "100%" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <h3 style={{ margin: 0, color: "var(--deep-blue)" }}>
            Question Versions
          </h3>
          <button className="btn-secondary" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* List */}
        <div style={{ marginBottom: 14 }}>
          {loading ? (
            <div>Loading versions…</div>
          ) : versions.length ? (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              {versions.map((v, i) => {
                // Newest-first numbering: newest gets highest vN
                const vNum = versions.length - i;
                const isCurrent = v.id === selectedVersionId;
                return (
                  <li key={v.id} style={itemCardStyle(isCurrent)}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 12,
                        marginBottom: 6,
                      }}
                    >
                      <strong style={{ color: "var(--dark-purple)" }}>
                        v{vNum}
                      </strong>
                      <small style={{ color: "#666" }}>
                        {v.created_at ? formatDateTime(v.created_at) : ""}
                      </small>
                    </div>

                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.5,
                        marginBottom: 8,
                      }}
                    >
                      {v.text}
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {isCurrent ? (
                        <span style={pressedPillStyle} aria-pressed="true">
                          ✓ Selected
                        </span>
                      ) : (
                        <button
                          className="btn-primary"
                          onClick={() => handleSelect(v.id)}
                          title="Select this version"
                        >
                          Select
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div style={{ color: "#666" }}>No versions yet.</div>
          )}
        </div>

        {/* Create new version */}
        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 14 }}>
          <h4 style={{ margin: "0 0 8px 0", color: "var(--dark-purple)" }}>
            Create New Version
          </h4>
          <textarea
            rows={6}
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Enter new text for this question…"
            style={{
              width: "100%",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 10,
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 10,
            }}
          >
            <button className="btn-secondary" onClick={onClose}>
              Close
            </button>
            <button onClick={handleAdd} className="btn-primary">
              Save & Select
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
